import { useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import { useGetApiV1MediaMediaIdLink } from '@/shared/api/generated/media/media';
import { ChatMessageType } from '@/shared/api/generated/model';
import { useLocale } from '@/shared/lib/i18n/locale-context';
import { Button } from '@/shared/ui/Button';

/**
 * A recording, a photograph or a clip attached to a chat message.
 *
 * ⚠️ **The link is fetched when somebody asks for it, never with the history.** §11 signs a media
 * URL with an expiry, so a link baked into a page of history is broken for every message older than
 * the signature — and a control room leaves this screen open for a whole shift. Worse, minting a
 * link is an AUDITED event (§12.9): loading fifty of them because fifty messages scrolled past
 * would record fifty accesses to a missing person's photographs that nobody actually looked at.
 *
 * ⚠️ So the default state is a named placeholder, not a spinner. The operator sees what KIND of
 * thing it is and how long it runs before deciding to open it, which is also the honest thing to
 * put in an audit trail: they asked for this one.
 */
export function MediaAttachment({
  mediaId,
  kind,
  icon: Icon,
  caption,
}: {
  mediaId: string;
  kind: string;
  icon: LucideIcon;

  /** Text the sender typed alongside the file. */
  caption?: string | null;
}) {
  const { t } = useLocale();
  const [opened, setOpened] = useState(false);

  const link = useGetApiV1MediaMediaIdLink(mediaId, { query: { enabled: opened } });

  const label =
    kind === ChatMessageType.Voice
      ? t.missions.chatVoice
      : kind === ChatMessageType.Image
        ? t.missions.chatImage
        : t.missions.chatVideo;

  return (
    <div className="flex flex-col gap-2">
      {opened && link.data?.url ? (
        <Attachment kind={kind} url={link.data.url} label={label} />
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <span className="flex items-center gap-2 text-theme-sm text-gray-700">
            <Icon aria-hidden className="size-4 text-brand-500" />
            {label}
          </span>

          <Button
            variant="ghost"
            size="sm"
            disabled={link.isFetching}
            onClick={() => setOpened(true)}
          >
            {link.isFetching ? t.app.loading : t.missions.chatOpenMedia}
          </Button>
        </div>
      )}

      {opened && link.isError ? (
        <p role="alert" className="text-theme-xs text-error-500">
          {t.missions.chatMediaFailed}
        </p>
      ) : null}

      {caption ? (
        <p className="whitespace-pre-wrap break-words text-theme-sm text-gray-700">{caption}</p>
      ) : null}
    </div>
  );
}

function Attachment({ kind, url, label }: { kind: string; url: string; label: string }) {
  if (kind === ChatMessageType.Image) {
    return (
      // ⚠️ Bounded, and `object-contain`. A photograph from a handset is several thousand pixels
      // wide; unbounded it takes the whole card and pushes the rest of the conversation off screen.
      <img
        src={url}
        alt={label}
        className="max-h-72 w-auto max-w-full rounded-md object-contain"
      />
    );
  }

  if (kind === ChatMessageType.Video) {
    return <video src={url} controls className="max-h-72 w-full rounded-md" aria-label={label} />;
  }

  // ⚠️ `preload="none"`: a voice note in a search is often somebody describing where they are, and
  // the browser fetching every one of them the moment it is rendered is bandwidth a control room on
  // a field connection does not have.
  return <audio src={url} controls preload="none" className="w-full" aria-label={label} />;
}
